/**
 * Configuration des étapes du tutoriel interactif de création d'attestation de livraison
 *
 * 1 étape par wizard step. La navigation entre steps se fait via le WizardFooter réel
 * (le tutoriel ne gère pas la navigation, il met en surbrillance les champs).
 *
 *   Step 1 (Contrat) → Step 2 (Garanties) → Step 3 (Éléments) → Step 4 (Véhicule)
 */

import type { DeliveryProofFormData } from '@/features/delivery-proof/stores/deliveryProofStore';
import { getVendorWording } from '@/utils/vendorWording';
import type { VendorWording } from '@/utils/vendorWording';

export interface TutorialStep {
  id: string;
  fieldRef: string | string[];
  title: string;
  content: string;
  validation: (formData: DeliveryProofFormData) => boolean;
  wizardStep: number;
  fieldType: 'text' | 'select' | 'radio' | 'number';
  placement?: 'auto' | 'top';
}

/**
 * Génère les étapes du tutoriel avec le wording adapté au vendorType (garantie/prestation)
 */
function buildTutorialSteps(wording: VendorWording): TutorialStep[] {
  return [
    // ========== Step 1 - Sélection du contrat ==========
    {
      id: 'contract-setup',
      fieldRef: ['dp-society-user-block', 'dp-contract-selector-block', 'dp-number-block'],
      title: 'Sélection du contrat',
      content: 'Choisissez le contrat ou brouillon pour l\'attestation. Vous pouvez aussi renseigner un numéro de bon de commande (facultatif).',
      validation: () => true,
      wizardStep: 1,
      fieldType: 'select',
    },

    // ========== Step 2 - Garanties/Prestations ==========
    {
      id: 'warranty',
      fieldRef: ['dp-warranty-toggle', 'dp-warranty-list'],
      title: `Informations ${wording.plural}`,
      content: `Cochez la case pour intégrer le bloc ${wording.pluralLower} dans l'attestation (facultatif). Sélectionnez les ${wording.pluralLower} proposées au client et masquez les prix si nécessaire.`,
      validation: () => true,
      wizardStep: 2,
      fieldType: 'select',
    },

    // ========== Step 3 - Éléments remis ==========
    {
      id: 'elements',
      fieldRef: ['dp-elements-toggle', 'dp-elements-list'],
      title: 'Éléments remis à la livraison',
      content: 'Cochez la case pour intégrer le bloc éléments remis (facultatif). Sélectionnez les éléments remis lors de la livraison (carte grise, clés, contrôle technique...). Vous pouvez ajouter des éléments personnalisés.',
      validation: () => true,
      wizardStep: 3,
      fieldType: 'select',
    },

    // ========== Step 4 - Informations véhicule ==========
    {
      id: 'vehicle',
      fieldRef: ['dp-vehicle-toggle', 'dp-vehicle-info-block'],
      title: 'Informations véhicule',
      content: 'Cochez la case pour intégrer le bloc véhicule (facultatif). Ajoutez le kilométrage, des photos et des observations. Cliquez ensuite sur "Prévisualiser" pour voir l\'attestation avant envoi.',
      validation: () => true,
      wizardStep: 4,
      fieldType: 'select',
    },
  ];
}

/**
 * Obtient les étapes du tutoriel (identiques pour tous les types d'utilisateur).
 * Pour les EXTERNAL, le bloc société/utilisateur n'est pas dans le DOM,
 * le bounding box ne couvre que les éléments présents.
 */
export function getFilteredTutorialSteps(vendorType?: number): TutorialStep[] {
  const wording = getVendorWording(vendorType);
  return buildTutorialSteps(wording);
}
